import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseServices } from '../../kuba.services';
import { KubaExportService } from '../../shared/kuba-exports/kuba-export.service';

/** One spreadsheet/PDF column: the visible heading and the row property it reads. */
export interface SdsExportColumn {
  title: string;
  dataKey: string;
}

export type SdsExportFormat = 'PDF' | 'XLSX' | 'CSV';

/**
 * Shared export plumbing for the Chemicals module, so every grid produces the
 * same file formats and naming instead of each page rolling its own (the
 * substitution page previously used the legacy `kuba-export` dropdown).
 */
@Injectable()
export class SdsExportService {
  constructor(private readonly kubaExport: KubaExportService) {}

  /**
   * Generates and downloads the export. CSV is produced client-side; XLSX and
   * PDF are rendered by the backend export endpoint.
   *
   * @param orientation 1 = landscape (wide grids), 0 = portrait.
   */
  run(
    format: SdsExportFormat,
    data: any[],
    columns: SdsExportColumn[],
    header: string,
    fileBase: string,
    orientation: number = 1
  ): Observable<void> {
    if (format === 'CSV') {
      this.downloadCsv(columns, data, fileBase);
      return of(void 0);
    }

    const ext = format === 'PDF' ? 'pdf' : 'xlsx';
    return this.kubaExport
      .generateExportFile(
        data,
        columns,
        format,
        encodeURIComponent(header),
        orientation,
        BaseServices.BusinessId,
        BaseServices.userCultureInfo()
      )
      .pipe(map((blob: Blob) => this.downloadBlob(blob, `${fileBase}-${this.stamp()}.${ext}`)));
  }

  /** dd/MM/yyyy for cells — raw ISO timestamps are unreadable in a spreadsheet. */
  formatDate(value: string | Date | null | undefined): string {
    if (!value) {
      return '';
    }

    const date = new Date(value as string);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${date.getFullYear()}`;
  }

  private downloadCsv(columns: SdsExportColumn[], data: any[], fileBase: string): void {
    const escape = (value: unknown): string => {
      const str = value == null ? '' : String(value);
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const lines: string[] = [columns.map(c => escape(c.title)).join(',')];
    for (const row of data) {
      lines.push(columns.map(c => escape(row[c.dataKey])).join(','));
    }

    // Leading BOM so Excel opens UTF-8 CSV with correct accents.
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    this.downloadBlob(blob, `${fileBase}-${this.stamp()}.csv`);
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  private stamp(): string {
    return new Date().toISOString().slice(0, 10);
  }
}
